/**
 * DocumentPageAssigner — Backend-driven generic page picker for multi-page documents
 *
 * DRR-179: Generic "pick pages from multi-page document + assign to categories"
 * component. Categories come from backend config (floors, sections, chapters).
 *
 * Two output modes:
 * 1. ImageExport — pages exported as PNG blobs (floor plans, diagrams)
 * 2. PageRange — page number + category only (contracts, regulations, intake)
 *
 * PdfFloorPlanPicker is the image-export specialization with auto-increment floors.
 * This is the generic version driven entirely by PageAssignmentConfig from the backend.
 *
 * Usage:
 *   const { config } = usePageAssignmentConfig('contract_sections');
 *   <DocumentPageAssigner config={config} pdfSource={file} onAssignment={...} />
 */

import { useState, useCallback } from 'react';
import {
  Stack,
  Group,
  Paper,
  Text,
  Badge,
  Button,
  SimpleGrid,
  Center,
  Loader,
  Box,
  Select,
  Alert,
} from '@mantine/core';
import {
  IconCheck,
  IconFile,
  IconBuilding,
  IconScale,
  IconGavel,
  IconFileImport,
} from '@tabler/icons-react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

import type {
  PageAssignmentConfig,
  PageAssignmentCategory,
  PageAssignmentMode,
} from '@/hooks/usePageAssignmentConfig';

// Configure worker — use CDN matching react-pdf's bundled pdfjs-dist version
// (same pattern as PDFViewer.tsx; local new URL() resolves wrong path under Vite)
pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Result of assigning a page — includes PNG blob only for ImageExport mode. */
export interface PageAssignment {
  /** PDF page number (1-indexed) */
  pageNumber: number;
  /** Category ID this page was assigned to */
  categoryId: string;
  /** Category display label */
  categoryLabel: string;
  /** Category color (for UI badges) */
  categoryColor: string | null;
  /** PNG blob — only populated in ImageExport mode */
  pngBlob: Blob | null;
}

interface DocumentPageAssignerProps {
  /** Backend-driven config from usePageAssignmentConfig() */
  config: PageAssignmentConfig;
  /** PDF file or blob URL to pick pages from */
  pdfSource: File | string;
  /** Callback fired when a page is assigned to a category */
  onAssignment: (assignment: PageAssignment) => void;
  /** Callback fired when an assignment is removed */
  onUnassign?: (pageNumber: number) => void;
  /** Already-assigned pages (for showing badges on re-render) */
  assignedPages?: Map<number, { categoryId: string; categoryLabel: string }>;
}

// ---------------------------------------------------------------------------
// Icon resolver — maps icon name from backend config to Tabler icon
// ---------------------------------------------------------------------------

const ICON_MAP: Record<string, React.FC<Record<string, unknown>>> = {
  building: IconBuilding,
  'file-text': IconFile,
  gavel: IconGavel,
  scale: IconScale,
  'file-import': IconFileImport,
  file: IconFile,
};

function resolveIcon(name: string): React.FC<Record<string, unknown>> {
  return ICON_MAP[name] ?? IconFile;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function DocumentPageAssigner({
  config,
  pdfSource,
  onAssignment,
  onUnassign,
  assignedPages = new Map(),
}: DocumentPageAssignerProps) {
  const [numPages, setNumPages] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState<number | null>(null);
  const [autoIncrementCounter, setAutoIncrementCounter] = useState(
    config.autoIncrement?.start ?? 1,
  );

  // For category-select mode: track which category the user has selected per page
  const [selectedCategories, setSelectedCategories] = useState<Map<number, string>>(new Map());

  const isAutoIncrement = config.autoIncrement?.enabled ?? false;
  const isImageExport = config.mode === 'IMAGE_EXPORT';
  const hasStaticCategories = config.categories.length > 0;

  const onDocumentLoadSuccess = useCallback(
    ({ numPages: n }: { numPages: number }) => {
      setNumPages(n);
      setLoading(false);
      // Auto-set counter based on already-assigned pages
      if (isAutoIncrement) {
        setAutoIncrementCounter((config.autoIncrement?.start ?? 1) + assignedPages.size);
      }
    },
    [assignedPages.size, config.autoIncrement?.start, isAutoIncrement],
  );

  const exportPageAsPng = useCallback(async (pageNumber: number): Promise<Blob | null> => {
    const canvasId = `page-assigner-page-${pageNumber}`;
    const canvas = document.getElementById(canvasId) as HTMLCanvasElement | null;
    if (!canvas) {
      console.error(`Canvas not found for page ${pageNumber}`);
      return null;
    }

    return new Promise<Blob>((resolve, reject) => {
      canvas.toBlob(
        (b) => (b ? resolve(b) : reject(new Error('Canvas toBlob failed'))),
        'image/png',
      );
    });
  }, []);

  const handleAssignPage = useCallback(
    async (pageNumber: number, category: PageAssignmentCategory) => {
      setExporting(pageNumber);
      try {
        let pngBlob: Blob | null = null;
        if (isImageExport) {
          pngBlob = await exportPageAsPng(pageNumber);
          if (!pngBlob) {
            setExporting(null);
            return;
          }
        }

        onAssignment({
          pageNumber,
          categoryId: category.id,
          categoryLabel: category.label,
          categoryColor: category.color,
          pngBlob,
        });

        if (isAutoIncrement) {
          setAutoIncrementCounter((prev) => prev + 1);
        }
      } finally {
        setExporting(null);
      }
    },
    [isImageExport, isAutoIncrement, exportPageAsPng, onAssignment],
  );

  const handleAutoIncrementAssign = useCallback(
    async (pageNumber: number) => {
      const template = config.autoIncrement?.labelTemplate ?? 'Item {n}';
      const label = template.replace('{n}', String(autoIncrementCounter));
      const category: PageAssignmentCategory = {
        id: `auto_${autoIncrementCounter}`,
        label,
        description: null,
        color: null,
        icon: null,
        sortOrder: autoIncrementCounter,
      };
      await handleAssignPage(pageNumber, category);
    },
    [config.autoIncrement?.labelTemplate, autoIncrementCounter, handleAssignPage],
  );

  const handleCategorySelectAssign = useCallback(
    async (pageNumber: number) => {
      const categoryId = selectedCategories.get(pageNumber);
      if (!categoryId) return;

      const category = config.categories.find((c) => c.id === categoryId);
      if (!category) return;

      await handleAssignPage(pageNumber, category);
      // Clear selection after assignment
      setSelectedCategories((prev) => {
        const next = new Map(prev);
        next.delete(pageNumber);
        return next;
      });
    },
    [selectedCategories, config.categories, handleAssignPage],
  );

  const handleCategoryChange = useCallback((pageNumber: number, categoryId: string | null) => {
    setSelectedCategories((prev) => {
      const next = new Map(prev);
      if (categoryId) {
        next.set(pageNumber, categoryId);
      } else {
        next.delete(pageNumber);
      }
      return next;
    });
  }, []);

  const fileSource =
    pdfSource instanceof File ? URL.createObjectURL(pdfSource) : pdfSource;

  const HeaderIcon = resolveIcon(config.ui.icon);

  // Build category select data for dropdown
  const categorySelectData = config.categories.map((c) => ({
    value: c.id,
    label: c.label,
  }));

  // Determine already-assigned category IDs (to optionally disable in dropdown)
  const assignedCategoryIds = new Set(
    Array.from(assignedPages.values()).map((a) => a.categoryId),
  );

  return (
    <Stack gap="sm">
      <Group gap="xs">
        <HeaderIcon size={16} />
        <Text fw={600} size="sm">
          {config.name} ({assignedPages.size} assigned)
        </Text>
      </Group>

      <Text size="xs" c="dimmed">
        {config.description}
      </Text>

      {loading && (
        <Center py="md">
          <Loader size="sm" />
          <Text size="xs" c="dimmed" ml="xs">
            Loading PDF...
          </Text>
        </Center>
      )}

      <Document file={fileSource} onLoadSuccess={onDocumentLoadSuccess} loading={null}>
        <SimpleGrid
          cols={{
            base: 1,
            sm: Math.min(config.ui.columns, 2),
            md: config.ui.columns,
          }}
          spacing="sm"
        >
          {Array.from({ length: numPages }, (_, i) => i + 1).map((pageNum) => {
            const assigned = assignedPages.get(pageNum);
            const isExportingPage = exporting === pageNum;
            const selectedCategoryId = selectedCategories.get(pageNum);

            return (
              <Paper
                key={pageNum}
                withBorder
                radius="md"
                p="xs"
                style={{
                  borderColor: assigned
                    ? `var(--mantine-color-${config.ui.assignedBadgeColor}-5)`
                    : 'var(--mantine-color-default-border)',
                  borderWidth: assigned ? 2 : 1,
                }}
              >
                {/* Page thumbnail rendered by react-pdf */}
                <Box
                  style={{
                    overflow: 'hidden',
                    borderRadius: 'var(--mantine-radius-sm)',
                    backgroundColor: 'white',
                  }}
                >
                  <Page
                    pageNumber={pageNum}
                    width={config.ui.thumbnailWidth}
                    scale={typeof window !== 'undefined' ? Math.min(window.devicePixelRatio || 1, 3) : 2}
                    renderTextLayer={false}
                    renderAnnotationLayer={false}
                    canvasRef={(canvas) => {
                      if (canvas) {
                        canvas.id = `page-assigner-page-${pageNum}`;
                        canvas.style.width = `${config.ui.thumbnailWidth}px`;
                        canvas.style.height = 'auto';
                      }
                    }}
                  />
                </Box>

                {/* Page info + assign controls */}
                <Stack gap={4} mt="xs">
                  <Group gap="xs" justify="space-between">
                    <Text size="xs" c="dimmed">
                      Page {pageNum}
                    </Text>

                    {assigned && (
                      <Group gap={4}>
                        <Badge
                          size="sm"
                          variant="filled"
                          color={config.ui.assignedBadgeColor}
                          leftSection={<IconCheck size={10} />}
                        >
                          {assigned.categoryLabel}
                        </Badge>
                        {onUnassign && (
                          <Button
                            size="compact-xs"
                            variant="subtle"
                            color="red"
                            onClick={() => onUnassign(pageNum)}
                          >
                            Remove
                          </Button>
                        )}
                      </Group>
                    )}
                  </Group>

                  {/* Assignment controls — only show for unassigned pages */}
                  {!assigned && (
                    <>
                      {isAutoIncrement ? (
                        /* Auto-increment mode: single button */
                        <Button
                          size="xs"
                          variant="light"
                          loading={isExportingPage}
                          onClick={() => handleAutoIncrementAssign(pageNum)}
                          fullWidth
                        >
                          {numPages === 1
                            ? config.ui.assignButtonLabel
                            : `${config.ui.assignButtonLabel} ${autoIncrementCounter}`}
                        </Button>
                      ) : hasStaticCategories ? (
                        /* Category select mode: dropdown + assign button */
                        <Group gap="xs" wrap="nowrap">
                          <Select
                            size="xs"
                            placeholder="Select category"
                            data={categorySelectData}
                            value={selectedCategoryId ?? null}
                            onChange={(v) => handleCategoryChange(pageNum, v)}
                            style={{ flex: 1 }}
                            clearable
                          />
                          <Button
                            size="xs"
                            variant="light"
                            loading={isExportingPage}
                            disabled={!selectedCategoryId}
                            onClick={() => handleCategorySelectAssign(pageNum)}
                          >
                            Assign
                          </Button>
                        </Group>
                      ) : (
                        /* No categories defined — show info */
                        <Alert variant="light" color="gray" p="xs">
                          <Text size="xs">No categories configured</Text>
                        </Alert>
                      )}
                    </>
                  )}
                </Stack>
              </Paper>
            );
          })}
        </SimpleGrid>
      </Document>
    </Stack>
  );
}
